/**
 * The supply audit (#1292) — walk the kitchen before the shop.
 *
 * Grouped by where a thing LIVES, not by aisle: fridge, freezer, cupboard,
 * spice rack. It is a different journey over the same items than the list
 * makes, and asking somebody to check the cupboard twice because the questions
 * are in shop order is how a good feature becomes a skipped one.
 *
 * ⚠️ "Have it" MARKS the line, it does not delete it. A list that silently
 * drops things is a list you stop trusting.
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { twoPlates, STORAGE_LABEL, type AuditQuestion } from "../api/twoPlates";

const STORAGE_ORDER = ["fridge", "freezer", "pantry", "spice"];

export default function Audit() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const id = Number(planId);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["audit", id],
    queryFn: () => twoPlates.audit(id),
    enabled: Number.isInteger(id),
  });

  const [have, setHave] = useState<Record<string, boolean>>({});

  const submit = useMutation({
    mutationFn: () =>
      twoPlates.answerAudit(
        id,
        (data?.ask ?? []).map((q) => ({ itemName: q.itemName, have: have[q.itemName] === true })),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan"] });
      navigate("/plan");
    },
  });

  if (isLoading) return <p className="text-muted text-sm">Checking what is worth asking…</p>;
  if (isError || !data) return <p className="text-warn text-sm">Could not load the audit.</p>;

  if (data.ask.length === 0) {
    return (
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Nothing to check.</h1>
        <p className="text-muted mt-2 text-sm">
          {data.skipped.perishable} of these are fresh and always bought, and{" "}
          {data.skipped.recentlyAnswered} you answered recently enough to trust.
        </p>
        <button
          onClick={() => navigate("/plan")}
          className="bg-plate-b text-ground tap mt-6 w-full rounded-lg font-semibold"
        >
          Back to the list
        </button>
      </div>
    );
  }

  const byStorage = STORAGE_ORDER.map((where) => ({
    where,
    items: data.ask.filter((q) => (q.storedIn ?? "pantry") === where),
  })).filter((g) => g.items.length > 0);

  const answered = data.ask.filter((q) => have[q.itemName] !== undefined).length;

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-xl font-semibold tracking-tight">Before you go</h1>
        <p className="text-muted mt-1 text-xs">
          {data.ask.length} worth checking of {data.totalItems}. The other{" "}
          {data.skipped.perishable} are fresh — nobody needs to be asked whether they
          have salmon.
        </p>
      </header>

      <div className="flex flex-col gap-6">
        {byStorage.map((group) => (
          <section key={group.where}>
            <h2 className="text-muted text-xs uppercase tracking-wide">
              {STORAGE_LABEL[group.where] ?? group.where}
            </h2>
            <ul className="mt-2 flex flex-col gap-2">
              {group.items.map((q) => (
                <Row
                  key={q.id}
                  q={q}
                  value={have[q.itemName]}
                  onAnswer={(v) => setHave((prev) => ({ ...prev, [q.itemName]: v }))}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>

      <div className="border-line bg-ground sticky bottom-16 mt-8 border-t pt-4">
        <button
          onClick={() => submit.mutate()}
          disabled={submit.isPending}
          className="bg-plate-b text-ground tap w-full rounded-lg font-semibold disabled:opacity-50"
        >
          {submit.isPending ? "Saving…" : `Done — ${answered} of ${data.ask.length} answered`}
        </button>
        <p className="text-muted mt-2 text-center text-[11px]">
          Anything you skip stays on the list.
        </p>
      </div>
    </div>
  );
}

function Row({
  q,
  value,
  onAnswer,
}: {
  q: AuditQuestion;
  value: boolean | undefined;
  onAnswer: (v: boolean) => void;
}) {
  return (
    <li className="border-line flex items-center justify-between gap-3 rounded-lg border p-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">{q.itemName}</p>
        <p className="text-muted mt-0.5 text-[11px]">{q.forMeals.join(", ")}</p>
      </div>
      <div className="flex shrink-0 gap-2">
        {[
          { label: "Have it", v: true },
          { label: "Need it", v: false },
        ].map((opt) => (
          <button
            key={opt.label}
            onClick={() => onAnswer(opt.v)}
            aria-pressed={value === opt.v}
            className="border-line rounded-lg border px-3 py-2 text-xs font-medium"
            style={
              value === opt.v
                ? {
                    borderColor: opt.v ? "var(--color-plate-b)" : "var(--color-plate-a)",
                    color: opt.v ? "var(--color-plate-b)" : "var(--color-plate-a)",
                  }
                : undefined
            }
          >
            {opt.label}
          </button>
        ))}
      </div>
    </li>
  );
}
